import React from 'react';
import {
    CheckButton,
    CheckButtonFontStyle,
    DropDownBox,
    DropDownBoxComponentSpacing,
    StateDropDown,
    TextBox,
    ToggleButton,
    ToggleButtonItem,
    ToggleButtonLabelSize,
    YearDropDown,
    YesNoToggleButton,
} from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import {
    EntityOwner,
    Owner,
    OwnerType,
    PeopleGroupOwner,
    PersonOwner,
    WorkTimeType,
} from '../types';
import { PersonNameFields } from './PersonNameFields';
import { asInput } from './inputValue';
import { formatEin } from '../utils/formatEin';
import {
    OFFICER_TITLE_OPTIONS,
    ROLE_OPTIONS,
    YEAR_INCORPORATED_START,
} from '../constants/options';

interface OwnerFormProps {
    idPrefix: string;
    owner: Owner;
    onChange: (patch: Partial<Owner>) => void;
}

const WORK_TIME_ITEMS: ToggleButtonItem[] = [
    { id: 'Full', text: 'Full', hint: 'Full time' },
    { id: 'Part', text: 'Part', hint: 'Part time' },
];

const workTimeToId = (value: WorkTimeType | null): string[] => {
    switch (value) {
        case WorkTimeType.Full:
            return ['Full'];
        case WorkTimeType.Part:
            return ['Part'];
        default:
            return [];
    }
};

export const OwnerForm: React.FC<OwnerFormProps> = ({ idPrefix, owner, onChange }) => {
    if (owner.ownerType === OwnerType.Person) {
        const person = owner as PersonOwner;
        const update = <K extends keyof PersonOwner>(key: K, value: PersonOwner[K]) =>
            onChange({ [key]: value } as Partial<Owner>);

        return (
            <>
                <PersonNameFields
                    idPrefix={idPrefix}
                    value={person}
                    requiredFields
                    onChange={(patch) => onChange(patch as Partial<Owner>)}
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                        <DropDownBox
                            id={`${idPrefix}Role`}
                            value={asInput(person.role)}
                            onChange={(v) => update('role', v)}
                            label='Role'
                            required={true}
                            dataSource={ROLE_OPTIONS}
                            valueExpr='value'
                            displayExpr='display'
                            componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                        />
                    </div>
                    <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                        <DropDownBox
                            id={`${idPrefix}OfficerTitle`}
                            value={asInput(person.officerTitle)}
                            onChange={(v) => update('officerTitle', v)}
                            label='Officer Title'
                            required={true}
                            dataSource={OFFICER_TITLE_OPTIONS}
                            valueExpr='value'
                            displayExpr='display'
                            componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                        />
                    </div>
                </div>

                <TextBox
                    id={`${idPrefix}Phone`}
                    value={asInput(person.phone)}
                    onChange={(v) => update('phone', v)}
                    label='Phone Number'
                    type='tel'
                    maxLength={20}
                />

                <TextBox
                    id={`${idPrefix}Email`}
                    value={asInput(person.email)}
                    onChange={(v) => update('email', v)}
                    label='Email Address'
                    type='email'
                    maxLength={200}
                />

                <div style={{ marginTop: 20 }}>
                    <ToggleButton
                        id={`${idPrefix}WorkTime`}
                        items={WORK_TIME_ITEMS}
                        selectedItems={workTimeToId(person.workTime)}
                        selectionMode={person.workTime === null ? 'none' : 'single'}
                        stylingMode='outlined'
                        onItemClick={(e: ButtonGroupTypes.ItemClickEvent) =>
                            update(
                                'workTime',
                                (e.itemData as ToggleButtonItem)?.id === 'Full'
                                    ? WorkTimeType.Full
                                    : WorkTimeType.Part,
                            )
                        }
                        width='230px'
                        height='40px'
                        label='Is this person full time or part time?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>

                <div style={{ marginTop: 20 }}>
                    <YesNoToggleButton
                        id={`${idPrefix}Guarantor`}
                        value={person.isWillingGuarantor ?? undefined}
                        onItemClick={(e) =>
                            update('isWillingGuarantor', (e.itemData as ToggleButtonItem)?.id === 'Yes')
                        }
                        width='230px'
                        height='40px'
                        label='Is this person willing to be a guarantor?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>
            </>
        );
    }

    if (owner.ownerType === OwnerType.Entity) {
        const entity = owner as EntityOwner;
        const update = <K extends keyof EntityOwner>(key: K, value: EntityOwner[K]) =>
            onChange({ [key]: value } as Partial<Owner>);

        return (
            <>
                <TextBox
                    id={`${idPrefix}LegalName`}
                    value={asInput(entity.legalName)}
                    onChange={(v) => update('legalName', v)}
                    label='Legal Entity Name'
                    required={true}
                    maxLength={100}
                />

                <TextBox
                    id={`${idPrefix}Ein`}
                    value={asInput(entity.ein.replace(/\D/g, ''))}
                    onChange={(v) => update('ein', formatEin(v))}
                    label='EIN / Tax Identification'
                    required={true}
                    mask='00-0000000'
                    maskInvalidMessage='EIN must be 9 digits'
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <YearDropDown
                            id={`${idPrefix}YearIncorporated`}
                            value={entity.yearIncorporated ?? undefined}
                            onChange={(v) => update('yearIncorporated', v ? Number(v) : null)}
                            label='Year Incorporated'
                            required={true}
                            yearStart={YEAR_INCORPORATED_START}
                        />
                    </div>
                    <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <StateDropDown
                            id={`${idPrefix}StateIncorporated`}
                            value={asInput(entity.stateIncorporated)}
                            onChange={(v) => update('stateIncorporated', v)}
                            label='State Incorporated'
                            required={true}
                            componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                        />
                    </div>
                </div>
            </>
        );
    }

    const group = owner as PeopleGroupOwner;
    const update = <K extends keyof PeopleGroupOwner>(key: K, value: PeopleGroupOwner[K]) =>
        onChange({ [key]: value } as Partial<Owner>);

    return (
        <div style={{ display: 'grid', gap: '16px' }}>
            <CheckButton
                id={`${idPrefix}InvestsInOwnName`}
                fontStyle={CheckButtonFontStyle.Large}
                label='I represent and warrant that each person in this group will invest in their own name and through an entity.'
                selected={group.investsInOwnName}
                onToggle={() => update('investsInOwnName', !group.investsInOwnName)}
                anchorCheckboxTop
            />
            <CheckButton
                id={`${idPrefix}NoSingleOwnerOverTwenty`}
                fontStyle={CheckButtonFontStyle.Large}
                label='I represent and warrant that among this group of people, a single individual will not directly or indirectly own or control more than 20% of the Borrower Entity and/or the collateral property.'
                selected={group.noSingleOwnerOverTwentyPercent}
                onToggle={() =>
                    update('noSingleOwnerOverTwentyPercent', !group.noSingleOwnerOverTwentyPercent)
                }
                anchorCheckboxTop
            />
            {/* TODO: Group entry form */}
        </div>
    );
};


export const emptyOwner = (ownerType: OwnerType, ownedEntityId: number): Owner => {
    const base = { id: 0, ownedEntityId, ownershipPercent: null };
    switch (ownerType) {
        case OwnerType.Person:
            return {
                ...base,
                ownerType: OwnerType.Person,
                personId: null,
                firstName: '',
                middleName: '',
                lastName: '',
                suffix: '',
                role: '',
                officerTitle: '',
                phone: '',
                email: '',
                workTime: null,
                isWillingGuarantor: null,
            };
        case OwnerType.Entity:
            return {
                ...base,
                ownerType: OwnerType.Entity,
                legalName: '',
                ein: '',
                yearIncorporated: null,
                stateIncorporated: '',
            };
        case OwnerType.PeopleGroup:
            return {
                ...base,
                ownerType: OwnerType.PeopleGroup,
                investsInOwnName: false,
                noSingleOwnerOverTwentyPercent: false,
            };
    }
};
